import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { useAuthStore } from '@/store/authStore';
import { Shield } from 'lucide-react';

export function TwoFactorChallengeModal() {
  const { pendingTwoFactor, verifyTwoFactorLogin, clearPendingTwoFactor, isLoading } = useAuthStore();
  const [code, setCode] = useState('');
  const [isBackupCode, setIsBackupCode] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!pendingTwoFactor) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!code) return;
    setError(null);
    try {
      await verifyTwoFactorLogin(code, isBackupCode);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
    }
  };

  const handleClose = () => {
    setCode('');
    setError(null);
    setIsBackupCode(false);
    clearPendingTwoFactor();
  };

  return (
    <Modal
      isOpen={true}
      onClose={handleClose}
      title="Two-Factor Authentication"
      size="sm"
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="flex items-center gap-2 text-muted-foreground">
          <Shield className="h-5 w-5" />
          <p className="text-sm">
            {isBackupCode
              ? 'Enter one of your backup codes'
              : 'Enter the 6-digit code from your authenticator app'
            }
          </p>
        </div>

        {error && (
          <Alert variant="destructive">{error}</Alert>
        )}

        <div className="space-y-1.5">
          <input
            type="text"
            inputMode={isBackupCode ? 'text' : 'numeric'}
            pattern={isBackupCode ? undefined : '[0-9]*'}
            maxLength={isBackupCode ? 8 : 6}
            value={code}
            onChange={e => setCode(isBackupCode ? e.target.value : e.target.value.replace(/\D/g, ''))}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm text-center tracking-widest font-mono text-lg"
            placeholder={isBackupCode ? 'backup code' : '000000'}
            autoComplete="one-time-code"
            autoFocus
          />
        </div>

        <Button
          type="submit"
          className="w-full"
          disabled={(!isBackupCode && code.length !== 6) || (isBackupCode && !code) || isLoading}
        >
          {isLoading ? 'Verifying...' : 'Verify'}
        </Button>

        <button
          type="button"
          className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => {
            setIsBackupCode(!isBackupCode);
            setCode('');
            setError(null);
          }}
        >
          {isBackupCode ? 'Use authenticator code instead' : 'Use a backup code instead'}
        </button>
      </form>
    </Modal>
  );
}
